import { create } from 'zustand';

import type { TradingSessionStatus } from '@/lib/api/tradingSession';

import { isTradingAuthorityActive } from './authority';

export type ReauthenticationReason =
    | 'manual'
    | 'expiring'
    | 'expired'
    | 'mutation';
export type TradingSessionUserMethod = 'eip712' | 'passkey';

/**
 * Why an operation is routing the user through the Connected Wallet window.
 * `security` covers owner-only reads and signatures; `anchorTransaction`
 * covers operations that go on to send a transaction on the authorization
 * anchor from the owner wallet.
 */
export interface ConnectedWalletRequirement {
    /** Sentence subject, e.g. "Passkey management". */
    operation: string;
    reason: 'security' | 'anchorTransaction';
}

interface TradingSessionStoreState {
    status: TradingSessionStatus | null;
    observedAtMs: number;
    clockNowMs: number;
    isLoading: boolean;
    /**
     * The status has been read at least once this page load (or reset by a
     * logout). Unlike `isLoading`, which flips on every focus refresh, this only
     * ever goes false→true: it is what lets the header hold back "Demo"/"Live"
     * and the identity button until there is an answer to show.
     */
    hasResolved: boolean;
    reauthenticationOpen: boolean;
    reauthenticationReason: ReauthenticationReason;
    /**
     * The method the open dialog runs. There is no picker: sign-in passes the
     * method the user tapped, and every other caller renews with the method
     * the current session was made with (the wallet when there is none).
     */
    preferredMethod: TradingSessionUserMethod;
    /**
     * Set when an operation (not the user) opened the Connected Wallet window:
     * the dialog explains the operation instead of a generic sign-in.
     */
    walletRequirement: ConnectedWalletRequirement | null;
    pendingJobId: string | null;
    applyStatus: (status: TradingSessionStatus, observedAtMs?: number) => void;
    setLoading: (loading: boolean) => void;
    tick: (localNowMs?: number) => void;
    requestReauthentication: (
        reason?: ReauthenticationReason,
        preferredMethod?: TradingSessionUserMethod,
    ) => void;
    /**
     * Opens the Connected Wallet window: wallet method locked, default
     * duration, signature requested as soon as the dialog can. Pass the
     * requirement when an operation (not the user) is asking for it.
     */
    requestConnectedWalletWindow: (
        requirement?: ConnectedWalletRequirement,
    ) => void;
    closeReauthentication: () => void;
    requestJobReauthorization: (jobId: string) => void;
    clearPendingJob: () => void;
    getServerNowMs: (localNowMs?: number) => number;
    getRemainingMs: (localNowMs?: number) => number | null;
    hasActiveAuthority: (localNowMs?: number) => boolean;
    reset: () => void;
}

const createInitialState = () => ({
    status: null,
    observedAtMs: 0,
    clockNowMs: Date.now(),
    isLoading: true,
    hasResolved: false,
    reauthenticationOpen: false,
    reauthenticationReason: 'manual' as const,
    preferredMethod: 'eip712' as const,
    walletRequirement: null,
    pendingJobId: null,
});

/** The user method a renewal should reuse; the wallet when there is none. */
function sessionMethod(
    status: TradingSessionStatus | null,
): TradingSessionUserMethod {
    return status?.method === 'passkey' ? 'passkey' : 'eip712';
}

export const useTradingSessionStore = create<TradingSessionStoreState>(
    (set, get) => ({
        ...createInitialState(),
        applyStatus: (status, observedAtMs = Date.now()) =>
            set({
                status,
                observedAtMs,
                clockNowMs: observedAtMs,
                isLoading: false,
                hasResolved: true,
            }),
        setLoading: (isLoading) =>
            set(isLoading ? { isLoading } : { isLoading, hasResolved: true }),
        tick: (clockNowMs = Date.now()) => set({ clockNowMs }),
        requestReauthentication: (
            reauthenticationReason = 'manual',
            preferredMethod = sessionMethod(get().status),
        ) =>
            set({
                reauthenticationOpen: true,
                reauthenticationReason,
                preferredMethod,
                walletRequirement: null,
            }),
        requestConnectedWalletWindow: (requirement) =>
            set({
                reauthenticationOpen: true,
                reauthenticationReason: 'manual',
                preferredMethod: 'eip712',
                walletRequirement: requirement ?? null,
            }),
        closeReauthentication: () =>
            set({
                reauthenticationOpen: false,
                walletRequirement: null,
            }),
        requestJobReauthorization: (pendingJobId) =>
            set({
                pendingJobId,
                reauthenticationOpen: true,
                reauthenticationReason: 'mutation',
                preferredMethod: sessionMethod(get().status),
                walletRequirement: null,
            }),
        clearPendingJob: () => set({ pendingJobId: null }),
        getServerNowMs: (localNowMs = get().clockNowMs) => {
            const { status, observedAtMs } = get();
            return status
                ? localNowMs + status.serverTimeMs - observedAtMs
                : localNowMs;
        },
        getRemainingMs: (localNowMs = get().clockNowMs) => {
            const { status } = get();
            if (!status || typeof status.expiresAtMs !== 'number') return null;
            return Math.max(
                0,
                status.expiresAtMs - get().getServerNowMs(localNowMs),
            );
        },
        hasActiveAuthority: (localNowMs = get().clockNowMs) =>
            isTradingAuthorityActive(
                get().status,
                get().getServerNowMs(localNowMs),
            ),
        reset: () =>
            set({
                ...createInitialState(),
                isLoading: false,
                hasResolved: true,
            }),
    }),
);
